import { inject, injectable } from "@launchtray/tsyringe-async";
import { ILogger } from "../../cityversetycoon/logger/ILogger";
import { NFT } from "../nfts/NFT";
import { NFTBuilding } from "../nfts/NFTBuilding";
import { NFTGiftPackage } from "../nfts/NFTGiftPackage";
import { NFTPackage } from "../nfts/NFTPackage";
import { NFTCollectionsHelper } from "../utils/NFTCollectionsHelper";
import { IInventory } from "./IInventory";
import { Web3Inventory } from "./Web3Inventory";
import { Web3Config } from "../config/Web3Config";
import { createThirdwebClient } from "thirdweb";
import { NFTPiggyClub } from "../nfts/NFTPiggyClub";
import { INetworkClient } from "../../cityversetycoon/networking/INetworkClient";
import { PromiseCompletionSource } from "../../shared/async/PromiseCompletionSource";

@injectable()
export class InventoriesHolder implements IInventory {
    private readonly nftCollectionsHelper: NFTCollectionsHelper;
    private readonly inventory: Web3Inventory;
    private readonly externalInventories: Map<string, Web3Inventory>;

    private updatePiggyClubsPCS: PromiseCompletionSource<ReadonlyArray<NFTPiggyClub>>;
    private piggyClubs: Array<NFTPiggyClub>;

    public get hasBurnableDiceRolls(): boolean {
        return this.inventory.hasBurnableDiceRolls;//|| Array.from(this.externalInventories.values()).some((inventory) => inventory.hasBurnableDiceRolls);
    }

    public get buildingsNFT(): readonly NFTBuilding[] {
        return Array.from(this.externalInventories.values()).flatMap((inventory) => inventory.buildingsNFT).concat(this.inventory.buildingsNFT);
    }

    public get packagesNFT(): readonly NFTPackage[] {
        // return Array.from(this.externalInventories.values()).flatMap((inventory) => inventory.packagesNFT).concat(this.inventory.packagesNFT);
        return this.inventory.packagesNFT;
    }

    public get giftPackagesNFT(): readonly NFTGiftPackage[] {
        // return Array.from(this.externalInventories.values()).flatMap((inventory) => inventory.giftPackagesNFT).concat(this.inventory.giftPackagesNFT);
        return this.inventory.giftPackagesNFT;
    }

    public get piggyClubsNFT(): readonly NFTPiggyClub[] {
        return this.piggyClubs;
    }

    public set mainWalletAddress(value: string) {
        this.inventory.walletAddress = value;
    }

    public constructor(
        web3Config: Web3Config,
        @inject("INetworkClient")
        private readonly networkClient: INetworkClient,
        @inject("ILogger")
        private readonly logger: ILogger
    ) {
        const thirdWebClient = createThirdwebClient({ clientId: web3Config.thirdWebclientId });
        this.nftCollectionsHelper = new NFTCollectionsHelper(web3Config, thirdWebClient, this.logger);
        this.inventory = new Web3Inventory(this.nftCollectionsHelper, this.logger);
        this.externalInventories = new Map();
        this.piggyClubs = [];
    }

    public async update(): Promise<ReadonlyArray<NFT>> {
        // const operations: Array<Promise<ReadonlyArray<NFT>>> = Array.from(this.externalInventories.values()).map((inventory) => inventory.update());
        // operations.push(this.inventory.update());
        // return (await Promise.all(operations)).flat();

        const result = [];
        for (const inventory of this.externalInventories.values()) {
            result.push(...await inventory.updateBuildings());
        }
        result.push(...(await Promise.all([this.inventory.update(), this.updatePiggyClubs()])).flat());
        return result;
    }

    public async updateBuildings(): Promise<ReadonlyArray<NFTBuilding>> {
        const result = [];
        for (const inventory of this.externalInventories.values()) {
            result.push(...await inventory.updateBuildings());
        }
        result.push(...await this.inventory.updateBuildings());
        return result;
    }

    public async updatePackages(): Promise<ReadonlyArray<NFTPackage>> {
        // const operations: Array<Promise<ReadonlyArray<NFTPackage>>> = Array.from(this.externalInventories.values()).map((inventory) => inventory.updatePackages());
        // operations.push(this.inventory.updatePackages());
        // return (await Promise.all(operations)).flat();
        return await this.inventory.updatePackages();
    }

    public async updateGiftPackages(): Promise<ReadonlyArray<NFTGiftPackage>> {
        // const operations: Array<Promise<ReadonlyArray<NFTGiftPackage>>> = Array.from(this.externalInventories.values()).map((inventory) => inventory.updateGiftPackages());
        // operations.push(this.inventory.updateGiftPackages());
        // return (await Promise.all(operations)).flat();
        return await this.inventory.updateGiftPackages();
    }

    public buildingExist(tokenId: BigInt): boolean {
        return this.inventory.buildingExist(tokenId) || Array.from(this.externalInventories.values()).some((inventory) => inventory.buildingExist(tokenId));
    }

    public buildingExistOnInternalWallet(tokenId: BigInt): boolean {
        return this.inventory.buildingExist(tokenId);
    }

    public getPackageQuantityOwned(tokenId: BigInt): BigInt {
        return this.inventory.getPackageQuantityOwned(tokenId);
    }

    public removePackage(tokenId: BigInt): void {
        this.inventory.removePackage(tokenId);
    }

    public getGiftPackageQuantityOwned(tokenId: BigInt): BigInt {
        return this.inventory.getGiftPackageQuantityOwned(tokenId);
    }

    public removeGiftPackage(tokenId: BigInt): void {
        this.inventory.removeGiftPackage(tokenId);
    }

    public addInventory(address: string): void {
        if (this.externalInventories.has(address)) {
            this.logger.error(`Wallet address ${address} already defined`);
            return;
        }
        const inventory = new Web3Inventory(this.nftCollectionsHelper, this.logger);
        inventory.walletAddress = address;
        this.externalInventories.set(address, inventory);
    }

    public removeInventory(address: string): void {
        if (!this.externalInventories.has(address)) {
            this.logger.error(`Unable to remove inventory, wallet address ${address} not defined`);
            return;
        }
        this.externalInventories.get(address).clear();
        this.externalInventories.delete(address);
    }

    public walletAddressExist(address: string): boolean {
        return this.externalInventories.has(address);
    }

    public async updatePiggyClubs(): Promise<ReadonlyArray<NFTPiggyClub>> {
        try {
            if (this.updatePiggyClubsPCS != null) {
                return await this.updatePiggyClubsPCS.result;
            }
            this.updatePiggyClubsPCS = new PromiseCompletionSource();
            const nfts = await this.networkClient.getPiggyClubNFTs();
            if (nfts == null) {
                this.logger.debug("Update piggy clubs failed, used old piggy clubs data");
            }
            else {
                this.piggyClubs = nfts.items?.map((nft) => new NFTPiggyClub(nft));
                this.logger.debug(`Wallets piggy clubs updated: ${this.piggyClubs}`);
            }
        }
        catch (error: unknown) {
            this.logger.debug(`Unable to update piggy clubs, internal error after reteive nfts: ${error}`);
        }
        finally {
            this.updatePiggyClubsPCS?.complete(this.piggyClubs);
            this.updatePiggyClubsPCS = null;
            return this.piggyClubs;
        }
    }

    public clear(): void {
        this.inventory.clear();
        for (const inventory of this.externalInventories.values()) {
            inventory.clear();
        }
        this.externalInventories.clear();
        this.piggyClubs.length = 0;
    }
}